import type { FilterListItemData } from '@tanstack/react-router';

type FilterListItem = Omit<FilterListItemData, 'filterId' | 'id'> & { isPending?: boolean };
type FilterListItems = FilterListItem | FilterListItem[];

export interface ColumnFilter {
  id: string;
  value: unknown;
}

export type FilterListAdapterItem = FilterListItemData & { isPending?: boolean };

export interface FilterListAdapterResult {
  items: FilterListAdapterItem[];
  remove: (keys: Iterable<string | number>, columnFilters: ColumnFilter[]) => ColumnFilter[];
}

type FilterAdapterDefinition<
  TFilters extends object,
  TCtx = never,
> = {
  [K in keyof TFilters]: [TCtx] extends [never]
    ? (value: TFilters[K]) => FilterListItems
    : (value: TFilters[K], ctx: TCtx) => FilterListItems;
};

export function defineFilterListAdapter<
  TFilters extends object,
  TCtx = never,
>(
  definition: FilterAdapterDefinition<TFilters, TCtx>,
) {
  return (
    filters: ColumnFilter[],
    ...args: [TCtx] extends [never] ? [] : [ctx: TCtx]
  ): FilterListAdapterResult => {
    const items: FilterListAdapterItem[] = [];

    for (const filter of filters) {
      const handler = definition[filter.id as keyof TFilters] as
        | ((value: unknown, ctx: unknown) => FilterListItems)
        | undefined;

      if (!handler) {
        continue;
      }

      const result = handler(filter.value, args[0]);
      const entries = Array.isArray(result) ? result : [ result ];

      for (const entry of entries) {
        items.push({
          ...entry,
          filterId: filter.id,
          id: entry.filterValue !== undefined ? `${filter.id}-${entry.filterValue}` : filter.id,
        });
      }
    }

    return {
      items,
      remove: (keys, columnFilters) => {
        const removedItems = [ ...keys ]
          .map((key) => items.find((i) => i.id === String(key)))
          .filter((item): item is FilterListAdapterItem => item != null);

        return columnFilters.flatMap((filter) => {
          const item = removedItems.find((i) => i.filterId === filter.id);

          if (!item) {
            return [ filter ];
          }

          if (item.filterValue !== undefined && Array.isArray(filter.value)) {
            const remaining = (filter.value as unknown[]).filter((v) => v !== item.filterValue);
            return remaining.length > 0 ? [{ ...filter, value: remaining }] : [];
          }

          return [];
        });
      },
    };
  };
}
